import { Client, getStateCallbacks, Room } from "colyseus.js";
import { Debug, LogCategory } from '../../../shared/Debug';

export interface GameOptions {
  avatarIndex: number;
  gameMode: "open" | "private";
  gameCode?: string;
  playerName: string;
  networkManager?: NetworkManager;
}

// Type definitions for callbacks with priorities
interface StateChangeCallback {
  callback: (state: any) => void;
  priority: number;
}

export class NetworkManager {
  private client: Client;
  private gameRoom: Room | null = null;
  private disconnectDialog: HTMLDivElement | null = null;
  private reconnectionToken: string | null = null;
  
  private onRoomConnectedCallbacks: Array<(roomId: string) => void> = [];
  private onStateChangeCallbacks: Array<StateChangeCallback> = [];
  private onPlayerAddedCallbacks: Array<(player, key) => void> = [];
  private onPlayerRemovedCallbacks: Array<(player, key) => void> = [];
  private onBasketCallbacks: Array<(message) => void> = [];
  private onGameStartedCallbacks: Array<() => void> = [];
  private onGamePausedCallbacks: Array<() => void> = [];
  private onGameOverCallbacks: Array<(data: any) => void> = [];
  private audioContext: AudioContext | null = null;
  private audioQueue: ArrayBuffer[] = [];
  private isPlayingAudio: boolean = false;
  private readonly MAX_QUEUE_SIZE = 5; // Maximum number of audio items to keep in queue

  private heartbeatInterval: number | null = null;
  private readonly HEARTBEAT_INTERVAL = 30000; // 30 seconds

  constructor() {
    
    // Create Colyseus client using the same host as the client files
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const port = window.location.hostname === "localhost" || window.location.hostname.includes("192.168") ? 2567 : 80;
    const endpoint = `${wsProtocol}//${window.location.hostname}:${port}`;
    Debug.info(`Connecting to ${endpoint}`, LogCategory.GENERAL);
    this.client = new Client(endpoint);

    // Initialize audio context on user interaction
    document.addEventListener(
      "click",
      () => {
        if (!this.audioContext) {
          this.audioContext = new AudioContext();
        }
      },
      { once: true },
    );

    this.createDisconnectDialog();
  }

  private createDisconnectDialog() {
    // Create background overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 999;
    `;
    
    // Create dialog element
    this.disconnectDialog = document.createElement('div');
    this.disconnectDialog.style.cssText = `
      display: none;
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #2C2C2C;
      color: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.5);
      z-index: 1000;
      text-align: center;
      min-width: 300px;
    `;

    // Create message element
    const message = document.createElement('p');
    message.style.cssText = `
      margin-bottom: 20px;
      color: white;
      font-size: 16px;
    `;
    this.disconnectDialog.appendChild(message);

    // Create reload button
    const reloadButton = document.createElement('button');
    reloadButton.textContent = 'Reload Game';
    reloadButton.style.cssText = `
      background: #4CAF50;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 16px;
      transition: background-color 0.2s;
    `;
    reloadButton.onmouseover = () => reloadButton.style.backgroundColor = '#45a049';
    reloadButton.onmouseout = () => reloadButton.style.backgroundColor = '#4CAF50';
    reloadButton.onclick = () => window.location.reload()
    this.disconnectDialog.appendChild(reloadButton);

    // Add overlay and dialog to document
    document.body.appendChild(overlay);
    document.body.appendChild(this.disconnectDialog);

    // Store overlay reference in dialog for showing/hiding
    this.disconnectDialog.dataset.overlay = overlay.id = 'disconnect-overlay';
  }

  private async attemptReconnect() {
    this.gameRoom = null;
    if (this.reconnectionToken) {
      Debug.info("Reconnecting to server with token: " + this.reconnectionToken, LogCategory.GENERAL);

      try {
        this.gameRoom  = await this.client.reconnect(this.reconnectionToken);

        if (this.gameRoom !== null) {
            this.internalOnRoomConnected();
        }

      } catch (error) {
        Debug.error("Failed to reconnect to server: " + error, LogCategory.GENERAL);
        return false;
      }
    }

    return this.gameRoom !== null;
  }

  private showDisconnectDialog(error?: string) {
    if (this.disconnectDialog) {
      const message = this.disconnectDialog.querySelector('p');
      if (message) {
        message.textContent = error 
          ? `Disconnected from server: ${error}`
          : 'Disconnected from server';
      }
      this.disconnectDialog.style.display = 'block';
      const overlay = document.getElementById('disconnect-overlay');
      if (overlay) overlay.style.display = 'block';
    }
  }

  private hideDisconnectDialog() {
    if (this.disconnectDialog) {
      this.disconnectDialog.style.display = 'none';
      const overlay = document.getElementById('disconnect-overlay');
      if (overlay) overlay.style.display = 'none';
    }
  }

  private getDisconnectMessage(code: number): string {
    switch (code) {
      case 1000:
        return "Normal disconnection";
      case 1001:
        return "Server is going down or browser navigated away";
      case 1006:
        return "Connection lost - please check your internet connection";
      case 4000:
        return "Server room closed or timed out";
      case 4001:
        return "Client kicked from room";
      case 4002:
        return "Server room exceeded maximum number of clients";
      case 4003:
        return "Server rejected client authentication";
      case 4004:
        return "Server room match making failed";
      case 4005:
        return "Server room crashed";
      case 4006:
        return "Server room game mode invalid";
      case 4007:
        return "Server room not found";
      default:
        return `Connection closed (code: ${code})`;
    }
  }

  public async connect(options: GameOptions): Promise<void> {
    try {
      // Hide disconnect dialog if it was showing
      this.hideDisconnectDialog();

      // Determine room name based on game type
      const roomName = "basketball"; //options.gameType === '1on1' ? 'basketball_1v1' : 'basketball_3v3';

      // Connect to room
      if (options.gameMode === "private" && options.gameCode) {
        // Join existing private room
        this.gameRoom = await this.client.joinById(options.gameCode, {
          avatarIndex: options.avatarIndex,
          playerName: options.playerName,
        });
      } else if (options.gameMode === "private") {
        // Create new private room
        this.gameRoom = await this.client.create(roomName, {
          avatarIndex: options.avatarIndex,
          playerName: options.playerName,
          private: true,
        });
      } else {
        // Join or create an open room
        this.gameRoom = await this.client.joinOrCreate(roomName, {
          avatarIndex: options.avatarIndex,
          playerName: options.playerName,
        });
      }

      this.internalOnRoomConnected();

    } catch (error) {
        Debug.error("Failed to connect to game server: " + error, LogCategory.GENERAL);
        if (!this.attemptReconnect()) {
          this.showDisconnectDialog(error instanceof Error ? error.message : String(error));
        }
        return Promise.reject(error);
      }
    }

    private startHeartbeat() {
    // Clear any existing heartbeat
    this.stopHeartbeat();
    
    // Set up a new heartbeat interval
    this.heartbeatInterval = window.setInterval(() => {
      if (this.gameRoom) {
        Debug.info("Sending heartbeat to server", LogCategory.NETWORK);
        this.gameRoom.send("heartbeat", { timestamp: Date.now() });
      } else {
        // If we somehow lost the game room reference, stop the heartbeat
        this.stopHeartbeat();
      }
    }, this.HEARTBEAT_INTERVAL);
    
    Debug.info("Started heartbeat interval", LogCategory.NETWORK);
  }
  
  private stopHeartbeat() {
    if (this.heartbeatInterval !== null) {
      window.clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      Debug.info("Stopped heartbeat interval", LogCategory.NETWORK);
    }
  }

  private internalOnRoomConnected() {

      this.reconnectionToken = this.gameRoom.reconnectionToken;
    
      // Set up message handlers
      this.setupMessageHandlers();

      // Start the heartbeat to keep the connection alive
      this.startHeartbeat();

      // Get room ID and notify listeners
      const roomId = this.getRoomId();
      if (roomId) {
        Debug.info("Connected to room with ID: " + roomId, LogCategory.GENERAL);
        this.onRoomConnectedCallbacks.forEach((callback) => callback(roomId));
      }

      // Add disconnect handler
      this.gameRoom.onLeave((code) => {
        Debug.error(`Disconnected from server with code ${code}`, LogCategory.GENERAL);
        this.stopHeartbeat();
        if (!this.attemptReconnect()) {
          this.showDisconnectDialog(this.getDisconnectMessage(code));
        }
      });

      return Promise.resolve();    
  }

  /**
   * Clear the audio queue and stop any pending playback
   */
  private clearAudioQueue() {
    // Reset the queue
    this.audioQueue = [];
    
    // Reset playing state if needed
    if (this.isPlayingAudio) {
      this.isPlayingAudio = false;
      
      // Close and recreate audio context if needed
      if (this.audioContext) {
        this.audioContext.close().catch(error => {
          Debug.error("Error closing audio context:", error);
        });
        
        // Create a new context if needed later
        this.audioContext = null;
      }
    }
    
    Debug.info("Audio queue cleared", LogCategory.GENERAL);
  }

  public disconnect(): void {
    // Clear audio queue before disconnecting
    this.clearAudioQueue();
    
    // Stop the heartbeat
    this.stopHeartbeat();
    
    if (this.gameRoom) {
      this.gameRoom.leave();
      this.gameRoom = null;
    }
    // Don't show disconnect dialog for intentional disconnects
  }

  public sendPlayerInput(input: {
    movement?: { x: number; y: number };
    shoot?: boolean;
    pass?: { x: number; y: number };
    block?: boolean;
  }): void {
    if (this.gameRoom) {
      this.gameRoom.send("player_input", input);
    }
  }

  private setupMessageHandlers(): void {
    if (!this.gameRoom) return;

    const $ = getStateCallbacks(this.gameRoom);

    $(this.gameRoom.state).players.onAdd((player, key) => {
      Debug.info(`player added with sessionId (${key})`, LogCategory.PLAYER);
      Debug.info(player, LogCategory.PLAYER);
      this.onPlayerAddedCallbacks.forEach((callback) => callback(player, key));
    });

    $(this.gameRoom.state).players.onRemove((player, key) => {
      Debug.info(`player removed with sessionId (${key})`, LogCategory.PLAYER);
      Debug.info(player, LogCategory.PLAYER);
      this.onPlayerRemovedCallbacks.forEach((callback) =>
        callback(player, key),
      );
    });

    // Listen for state changes with prioritized processing
    this.gameRoom.onStateChange((state) => {
      // Notify any registered state change callbacks in priority order
      if (this.onStateChangeCallbacks) {
        // Process callbacks in priority order (already sorted when registered)
        this.onStateChangeCallbacks.forEach((callbackObj) => {
          try {
            callbackObj.callback(state);
          } catch (error) {
            Debug.error(`Error in state change callback: ${error}`, LogCategory.NETWORK);
          }
        });
      }
    });

    // Listen for game-specific messages
    this.gameRoom.onMessage("gameStarted", () => {
      this.onGameStartedCallbacks.forEach((callback) => callback());
    });

    this.gameRoom.onMessage("gamePaused", () => {
      this.onGamePausedCallbacks.forEach((callback) => callback());
    });

    this.gameRoom.onMessage("gameOver", (data) => {
      this.onGameOverCallbacks.forEach((callback) => callback(data));
    });

    this.gameRoom.onMessage("basket", (message) => {
      // Handle basket message
      Debug.info("basket: " + message, LogCategory.GENERAL);
      this.onBasketCallbacks.forEach((callback) => callback(message));
    });

    this.gameRoom.onMessage("commentary_audio", (audioData: any) => {
      Debug.info(`Received commentary audio of type: ${audioData.constructor.name}`, LogCategory.GENERAL);

      // Ensure audio context is initialized
      if (!this.audioContext) {
        try {
          this.audioContext = new AudioContext();
          Debug.info("Audio context initialized on commentary receive");
        } catch (error) {
          Debug.error("Failed to initialize audio context: " + error, LogCategory.GENERAL);
          return;
        }
      }

      // Check audio context state
      if (this.audioContext.state === "suspended") {
        Debug.info("Audio context is suspended, attempting to resume...", LogCategory.GENERAL);
        this.audioContext.resume().catch(console.error);
      }

      // Convert the received data to ArrayBuffer
      let arrayBuffer: ArrayBuffer;
      if (audioData instanceof Uint8Array) {
        arrayBuffer = audioData.buffer.slice(
          audioData.byteOffset,
          audioData.byteOffset + audioData.byteLength,
        );
      } else {
        Debug.error(
          "Received audio data is not a Uint8Array: " + JSON.stringify(audioData),
          LogCategory.NETWORK,
        );
        return;
      }

      // Enforce queue size limit - drop oldest items if queue is too large
      if (this.audioQueue.length >= this.MAX_QUEUE_SIZE) {
        // Remove oldest item to make room for the new one
        Debug.info(`Audio queue full (${this.audioQueue.length}), dropping oldest message`, LogCategory.GENERAL);
        this.audioQueue.shift();
      }
      
      // Add new audio to queue
      this.audioQueue.push(arrayBuffer);
      this.processAudioQueue();
    });
  }

  private async processAudioQueue() {
    Debug.info(`Processing audio queue. Queue length: ${this.audioQueue.length}`, LogCategory.GENERAL);
    Debug.info(`Is playing: ${this.isPlayingAudio}`, LogCategory.GENERAL);
    Debug.info(`Audio context state: ${this.audioContext?.state}`, LogCategory.GENERAL);

    if (!this.audioContext) {
      Debug.error("No audio context available", LogCategory.GENERAL);
      return;
    }

    if (this.audioContext.state === "suspended") {
      Debug.info("Audio context is suspended, attempting to resume...", LogCategory.GENERAL);
      try {
        await this.audioContext.resume();
      } catch (error) {
        Debug.error("Failed to resume audio context:", error);
        return;
      }
    }

    if (this.isPlayingAudio || this.audioQueue.length === 0) {
      return;
    }

    this.isPlayingAudio = true;
    const audioBuffer = this.audioQueue.shift()!;

    try {
      Debug.info(`Processing PCM audio data of size: ${audioBuffer.byteLength}`, LogCategory.GENERAL);
      
      // For PCM16 data, we need to create the AudioBuffer directly
      // Known parameters: 24000 Hz sample rate, 1 channel (mono)
      const pcmSampleRate = 24000;
      const pcmChannels = 1;
      
      if (audioBuffer.byteLength < 2) {
        Debug.warn("Audio buffer too small, skipping");
        this.isPlayingAudio = false;
        this.processAudioQueue();
        return;
      }
      
      // Create an AudioBuffer with the correct specifications
      const audioCtxBuffer = this.audioContext.createBuffer(
        pcmChannels,
        audioBuffer.byteLength / 2, // 16-bit = 2 bytes per sample
        pcmSampleRate
      );
      
      // Convert Uint8Array to Int16Array (PCM16 format)
      const int16Data = new Int16Array(audioBuffer);
      
      // Convert to Float32Array as required by Web Audio API
      const channelData = audioCtxBuffer.getChannelData(0);
      for (let i = 0; i < int16Data.length; i++) {
        // Convert 16-bit PCM to float in [-1.0, 1.0]
        channelData[i] = int16Data[i] / 32768.0;
      }
      
      Debug.info(`PCM audio processed successfully. Duration: ${audioCtxBuffer.duration}`, LogCategory.GENERAL);

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 1.0; // Adjust volume as needed

      source.buffer = audioCtxBuffer;
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      source.onended = () => {
        Debug.info("Audio playback completed");
        this.isPlayingAudio = false;
        this.processAudioQueue();
      };

      Debug.info("Starting audio playback...");
      source.start();
    } catch (error) {
      Debug.error("Error playing PCM audio:", error);
      this.isPlayingAudio = false;
      
      // Handle potential audio context issues
      if (error instanceof DOMException) {
        Debug.info("DOM Exception while playing audio, attempting to recreate audio context");
        if (this.audioContext) {
          this.audioContext.close().catch(console.error);
          this.audioContext = new AudioContext();
        }
      }
      
      // Continue processing the queue even if there was an error
      setTimeout(() => this.processAudioQueue(), 300);
    }
  }

  public getSessionId(): string | null {
    return this.gameRoom ? this.gameRoom.sessionId : null;
  }

  public getRoomId(): string | null {
    if (!this.gameRoom) {
      Debug.info("getRoomId: gameRoom is null", LogCategory.GENERAL);
      return null;
    }

    // Access the room id property correctly
    const roomId = this.gameRoom.roomId;

    Debug.info(`getRoomId called, room details - ID: ${roomId}, Name: ${this.gameRoom.name}, Session: ${this.gameRoom.sessionId}`, LogCategory.GENERAL);

    return roomId;
  }

  public sendMessage(type: string, data: any): void {
    if (this.gameRoom) {
      this.gameRoom.send(type, data);
    }
  }

  public onRoomConnected(callback: (roomId: string) => void): void {
    this.onRoomConnectedCallbacks.push(callback);

    // If we already have a room ID, call the callback immediately
    const roomId = this.getRoomId();
    if (roomId) {
      callback(roomId);
    }
  }

  /**
   * Add a callback for state changes with prioritization
   * @param callback The callback function to execute on state changes
   * @param priority The priority of this handler (higher = processed first)
   */
  public onStateChange(callback: (state: any) => void, priority: number = 0): void {
    // Add callback with priority information
    this.onStateChangeCallbacks.push({
      callback,
      priority
    });
    
    // Sort callbacks by priority (highest first)
    this.onStateChangeCallbacks.sort((a, b) => b.priority - a.priority);
  }

  public onPlayerAdded(callback: (player, key) => void): void {
    this.onPlayerAddedCallbacks.push(callback);
  }

  public onPlayerRemoved(callback: (player, key) => void): void {
    this.onPlayerRemovedCallbacks.push(callback);
  }

  public onBasket(callback: (message) => void): void {
    this.onBasketCallbacks.push(callback);
  }

  public onGameStarted(callback: () => void): void {
    this.onGameStartedCallbacks.push(callback);
  }

  public onGamePaused(callback: () => void): void {
    this.onGamePausedCallbacks.push(callback);
  }

  public onGameOver(callback: (data: any) => void): void {
    this.onGameOverCallbacks.push(callback);
  }


  /**
   * Sends a control message to the game announcer
   */
  public controlAnnouncer(action: 'enable' | 'disable' | 'reset' | 'set_probability', probability?: number): void {
    if (!this.gameRoom) return;
    
    const message: any = { action };
    if (action === 'set_probability' && typeof probability === 'number') {
      message.probability = probability;
    }
    
    this.gameRoom.send('announcer_control', message);
  }
}
